<template>
  <section class="flex flex-col gap-5">
    <form
      @submit.prevent="submit()"
      class="grid grid-cols-12 gap-5 lg:gap-8 text-black"
      autocomplete="off"
    >
      <div
        class="flex justify-between items-center gap-3 col-span-12 flex-wrap"
      >
        <h5>
          {{
            $t("labels.generic", {
              pt: "Informações da turma",
              en: "Class info",
            })
          }}
        </h5>
        <div class="flex items-center gap-3">
          <Button
            id="cancel-class-btn"
            :label="$t('labels.cancel')"
            class="btn-outline p-button-sm"
            @click="confirmModal()"
          />
          <Button
            id="continue-class-btn"
            :label="$t('labels.continue')"
            type="submit"
            class="btn-primary p-button-sm"
            :loading="loading"
          />
        </div>
      </div>

      <div class="col-span-12">
        <p class="mb-2">{{ $t("classes.create.inputs.name") }}</p>
        <InputText
          id="title-input"
          type="text"
          name="name"
          v-model="data.name"
          :class="v$.data.name.$error ? 'p-invalid' : ''"
          :maxlength="50"
        />
        <Errors
          :errors="v$.data.name.$errors"
          :actualLength="data.name?.length"
          :maxLength="50"
        />
      </div>

      <div class="col-span-12 flex flex-col gap-5">
        <h6>
          {{ $t("classes.create.inputs.select") }}
        </h6>

        <div
          class="bg-white border rounded-md"
          :class="v$.selected.$error ? 'border-red-500' : ''"
        >
          <div>
            <div class="p-3 border-b flex justify-between items-center">
              <span class="p-input-icon-right w-fit">
                <InputText
                  id="search-for-input"
                  type="text"
                  v-model="searchFor"
                  :placeholder="
                    $t('labels.generic', {
                      pt: 'Pesquisar curso',
                      en: 'Search course',
                    })
                  "
                  class="p-inputtext-sm"
                  @keyup="filterFor"
                />
                <i class="pi pi-search" />
              </span>
              <div class="flex items-center gap-3">
                <p class="hidden md:flex">
                  {{
                    $t("labels.generic", {
                      pt: "Selecionar toda a página",
                      en: "Select all page",
                    })
                  }}
                </p>
                <Checkbox
                  v-model="selectAll"
                  @change="selectAllCourses()"
                  :binary="true"
                />
              </div>
            </div>

            <div v-if="loader" class="py-32 bg-gray-100">
              <Loader :loader="loader" loaderSize="h-full" />
            </div>

            <div v-if="!loader" class="bg-gray-100 p-5 grid grid-cols-12 gap-3">
              <div
                v-for="(item, i) of courses"
                :key="i"
                class="col-span-12 sm:col-span-6 lg:col-span-4"
              >
                <div
                  class="bg-white p-3 flex flex-wrap justify-between items-center border-b-4 border-h-primary-500 rounded-md shadow-md overflow-hidden"
                >
                  <label
                    id="label-course"
                    class="w-10/12 cursor-pointer hover:opacity-80"
                    :title="item.name"
                    :for="item.id"
                  >
                    <div class="flex items-center gap-2">
                      <img
                        :src="
                          item.file?.url ||
                          'https://video-thumb.s3.sa-east-1.amazonaws.com/65197bdbplaceholder.png'
                        "
                        alt="course_img"
                        class="w-5/12 h-20 object-cover rounded"
                      />
                      <p class="line-clamp-2 w-7/12 font-[600]">
                        {{ item.name }}
                      </p>
                    </div>
                  </label>
                  <Checkbox
                    v-model="selected"
                    :inputId="item.id"
                    :value="item.id"
                  />
                </div>
              </div>

              <EmptyData
                :data="courses.length == 0"
                :label="emptyState.description"
                :image="emptyState.image"
                class="col-span-12"
                imgSize="h-40 mt-10"
                msgSize="md:w-5/12 mb-10"
              />
            </div>
            <div
              v-if="
                courseParams.total && courseParams?.total > courseParams?.size
              "
              class="flex justify-end items-center"
            >
              <Paginator
                :rows="courseParams?.size"
                :totalRecords="courseParams?.total"
                :rowsPerPageOptions="[courseParams?.size, 20, 30]"
                @page="paginate($event)"
              ></Paginator>
            </div>
          </div>
        </div>
        <Errors :errors="v$.selected.$errors" />
      </div>
    </form>
  </section>

  <ConfirmDialog />
</template>

<script lang="ts">
import { required } from "@/locale/locale.config";
import { Status } from "@/models/courses.model";
import { QueryParams } from "@/models/query-params.model";
import { CoursesRest } from "@/services/rest/courses.rest";
import { selectAll } from "@/utils/generics.util";
import { GoToRoute } from "@/utils/go-to.util";
import {
  getItemSessionStorage,
  setItemSessionStorage,
} from "@/utils/save-storage.util";
import useVuelidate from "@vuelidate/core";
import { debounceTime, take } from "rxjs";
import { defineComponent, PropType } from "vue";

export default defineComponent({
  name: "info",
  emits: ["onNext", "onAllow"],
  props: {
    classroom: {
      type: Object as PropType<any>,
    },
  },
  computed: {
    go(): GoToRoute {
      return new GoToRoute();
    },
    courseRest(): CoursesRest {
      return new CoursesRest();
    },
  },
  data() {
    return {
      loading: false as boolean,
      loader: true as boolean,
      searchFor: "" as string,
      data: {} as any,
      courses: [] as any,
      params: {
        id: this.$route.params.id ?? "",
        tab: 1,
      } as any,
      courseParams: new QueryParams(),
      selected: [] as any[],
      selectAll: false as any,
      emptyState: {
        image: "empty_courses.svg",
        description: this.$t("labels.generic", {
          pt: "Ainda não existem cursos publicados.",
          en: "There are no published courses yet.",
        }),
      } as any,
      status: Status.PUBLISHED.concat(",").concat(Status.NOT_LISTED) as string,
    };
  },
  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    return {
      data: {
        name: { required },
      },
      selected: { required },
    };
  },
  mounted() {
    this.getClassroom();
  },
  methods: {
    submit(): void {
      this.v$.data.$touch();
      this.v$.selected.$touch();
      if (!this.v$.data.$error && !this.v$.selected.$error) {
        this.loading = true;
        const isEdit = !!this.$route.params.id;
        const selectedUnique = Array.from(new Set(this.selected));

        const data = {
          ...this.data,
          coursesIds: isEdit ? undefined : selectedUnique,
          addedCoursesIds: isEdit
            ? selectedUnique.filter(
                (id) => !this.classroom.coursesIds.includes(id)
              )
            : undefined,
          removedCoursesIds: isEdit
            ? this.classroom.coursesIds?.filter(
                (id) => !selectedUnique.includes(id)
              )
            : undefined,
        };

        setItemSessionStorage("classData", JSON.stringify(data));
        setTimeout(() => this.nextStep(this.params.id), 200);
      }
    },
    paginate(event: { page: number; rows: number }): void {
      const params = new QueryParams(event.page, event.rows);
      this.selectAll = false;
      this.setPage(params);
    },
    setPage(queryParams: QueryParams): void {
      this.courseRest
        .getCourses(queryParams)
        .pipe(take(1), debounceTime(500))
        .subscribe({
          next: (response) => {
            this.courses = response.content;
            this.courseParams.total = response.total;
          },
          complete: () => {
            this.loader = false;
          },
        });
    },
    coursesPaginated(): void {
      const page = this.courseParams.page;
      const size = this.courseParams.size;
      const params = new QueryParams(page, size);
      params.status = this.status;
      this.setPage(params);
    },
    getClassroom(): void {
      const store = JSON.parse(getItemSessionStorage("classData"));
      if (this.$route.params.id && this.classroom && !store) {
        this.data = this.classroom;
        this.selected = this.classroom.coursesIds;
      } else if (store) {
        this.data = store;
        this.selected = this.classroom.coursesIds;
      }
      this.coursesPaginated();
    },
    selectAllCourses(): void {
      this.selected = selectAll(this.selectAll, this.courses, this.selected);
    },
    filterFor(): void {
      this.courseParams.name = this.searchFor;
      this.courseParams.status = this.status;
      this.setPage(this.courseParams);
    },
    confirmModal(event?: any): void {
      this.$confirm.require({
        message: this.$t("labels.generic", {
          pt: `Ao confirmar esta ação, você perderá os dados que não foram salvos.`,
          en: `By confirming this action, you will lose unsaved data.`,
        }),
        header: this.$t("labels.generic", {
          pt: "Sair e não salvar a turma?",
          en: "Exit Screen",
        }),
        acceptLabel: this.$t("labels.yes"),
        rejectLabel: this.$t("labels.no"),
        rejectClass: "btn-outline",
        acceptClass: "btn-delete-block",
        accept: () => {
          this.go.redirectToPage("classes");
        },
      });
    },
    nextStep(id?: any): void {
      this.params.id = id;
      this.$emit("onNext", 1);
      this.$emit("onAllow", 0);
      this.go.redirectToPage("formClasses", this.params);
      this.loading = false;
    },
  },
});
</script>
